import React, { useState, useEffect } from "react";
import { Phone, Clock, Play, Calendar, TrendingUp, BarChart3, Download } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Demo API key for testing - in production, this would come from auth context
const DEMO_API_KEY = "test_key_123";

export function UsagePage() {
  const [usageStats, setUsageStats] = useState([]);
  const [calls, setCalls] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30"); // days
  const [selectedCall, setSelectedCall] = useState(null);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { "X-API-Key": DEMO_API_KEY };
      
      const [statsRes, callsRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/usage/stats?days=${dateRange}`, { headers }),
        fetch(`${API_URL}/api/calls?limit=50`, { headers }),
        fetch(`${API_URL}/api/usage/summary`, { headers })
      ]);

      if (statsRes.ok) {
        setUsageStats(await statsRes.json());
      }
      if (callsRes.ok) {
        const data = await callsRes.json();
        setCalls(data.calls || []);
      }
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
    } catch (err) {
      console.error("Failed to fetch usage data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate chart data - group by day
  const chartData = usageStats.reduce((acc, stat) => {
    const date = stat.date?.split("T")[0] || stat.date;
    if (!acc[date]) {
      acc[date] = { date, minutes: 0, calls: 0 };
    }
    acc[date].minutes += stat.total_minutes || 0;
    acc[date].calls += stat.total_calls || 0;
    return acc;
  }, {});

  const chartDays = Object.values(chartData).sort((a, b) => a.date.localeCompare(b.date));
  const maxMinutes = Math.max(...chartDays.map(d => d.minutes), 1);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Usage & Calls</h1>
          <p className="text-slate-600 text-sm mt-1">Track your daily usage and call history</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg text-sm bg-white"
          >
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Minutes" 
          value={Math.round(summary?.total_minutes || 0)} 
          suffix="min"
          icon={Clock} 
          color="blue" 
        />
        <StatCard 
          title="Total Calls" 
          value={summary?.total_calls || 0} 
          icon={Phone} 
          color="green" 
        />
        <StatCard 
          title="Avg Call Duration" 
          value={Math.round(summary?.avg_duration_seconds / 60 || 0)} 
          suffix="min"
          icon={TrendingUp} 
          color="purple" 
        />
        <StatCard 
          title="This Month" 
          value={Math.round(summary?.current_month_minutes || 0)} 
          suffix="min"
          icon={Calendar} 
          color="orange" 
        />
      </div>

      {/* Day-by-Day Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-slate-900">Daily Usage</h3>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-blue-500"></div>
              <span className="text-slate-600">Minutes</span>
            </div>
          </div>
        </div>

        {chartDays.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No usage data for this period</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Chart */}
            <div className="flex items-end gap-1 h-48">
              {chartDays.map((day, idx) => {
                const height = (day.minutes / maxMinutes) * 100;
                return (
                  <div 
                    key={day.date || idx} 
                    className="flex-1 flex flex-col items-center group cursor-pointer"
                    title={`${day.date}: ${Math.round(day.minutes)} min, ${day.calls} calls`}
                  >
                    <div 
                      className="w-full bg-blue-500 rounded-t transition-all group-hover:bg-blue-600"
                      style={{ height: `${Math.max(height, 2)}%` }}
                    />
                  </div>
                );
              })}
            </div>
            {/* X-axis labels */}
            <div className="flex gap-1 text-xs text-slate-400">
              {chartDays.map((day, idx) => (
                <div key={day.date || idx} className="flex-1 text-center truncate">
                  {idx === 0 || idx === chartDays.length - 1 || idx % Math.ceil(chartDays.length / 7) === 0 
                    ? new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : ''
                  }
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Calls List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Recent Calls</h3>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">
              {calls.length}
            </span>
          </div>
        </div>

        {calls.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Phone className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No calls yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {calls.map((call, idx) => (
              <div 
                key={call.id || idx}
                className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => setSelectedCall(selectedCall?.id === call.id ? null : call)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      call.outcome === "booked" ? "bg-emerald-100 text-emerald-600" :
                      call.outcome === "lead" ? "bg-blue-100 text-blue-600" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {call.caller_number || call.from_number || "Unknown Caller"}
                      </p>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDuration(call.duration_seconds || call.duration || 0)}
                        </span>
                        <span>
                          {new Date(call.created_at || call.start_time).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {call.outcome && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        call.outcome === "booked" ? "bg-emerald-100 text-emerald-700" :
                        call.outcome === "lead" ? "bg-blue-100 text-blue-700" :
                        call.outcome === "info" ? "bg-purple-100 text-purple-700" :
                        "bg-slate-100 text-slate-700"
                      }`}>
                        {call.outcome}
                      </span>
                    )}
                    {call.recording_url && (
                      <a 
                        href={call.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 hover:bg-blue-100 rounded-lg text-blue-600"
                        title="Play Recording"
                      >
                        <Play className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Expanded Call Details */}
                {selectedCall?.id === call.id && (
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                    {call.summary && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Summary</p>
                        <p className="text-sm text-slate-700">{call.summary}</p>
                      </div>
                    )}
                    {call.transcript_summary && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Transcript Summary</p>
                        <p className="text-sm text-slate-700">{call.transcript_summary}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Call ID</p>
                        <p className="font-mono text-xs text-slate-700">{call.id?.slice(0, 12)}...</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Duration</p>
                        <p className="text-slate-700">{formatDuration(call.duration_seconds || call.duration || 0)}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Billed Minutes</p>
                        <p className="text-slate-700">{call.billed_minutes || Math.ceil((call.duration_seconds || 0) / 60)} min</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Status</p>
                        <p className="text-slate-700 capitalize">{call.status || "completed"}</p>
                      </div>
                    </div>
                    {call.recording_url && (
                      <div className="pt-2">
                        <a 
                          href={call.recording_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm font-medium"
                        >
                          <Play className="w-4 h-4" />
                          Listen to Recording
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ title, value, suffix, icon: Icon, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-orange-50 text-orange-600",
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-500 text-sm">{title}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">
            {value}{suffix && <span className="text-lg font-normal text-slate-500 ml-1">{suffix}</span>}
          </p>
        </div>
        <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return "0s";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

export default UsagePage;
